import { useState, useEffect } from 'react';
  const perms = detectPerms();
import {
  Plus,
  Trash2,
  X,
  CreditCard,
  Search,
  AlertCircle,
  Loader2,
  Check,
  Upload
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentOut = () => {
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [selectedBillId, setSelectedBillId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentType, setPaymentType] = useState('Cash');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [billsRes, txnRes, vendorsRes, pmRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/purchase-bills`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/transactions?type=payment_out`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/vendors`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/payment-modes`, getAuthHeaders())
      ]);
      setBills(billsRes.data);
      setTransactions(txnRes.data);
      setVendors(vendorsRes.data);
      setPaymentModes(pmRes.data.filter(p => p.active));
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const selectedBill = bills.find(b => b.id === selectedBillId);

  const openModal = () => {
    setSelectedVendorId('');
    setSelectedBillId('');
    setAmount('');
    setPaymentType('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setReferenceNumber('');
    setFormDescription('');
    setNotes('');
    setImageFile(null);
    setImageUrl('');
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/transactions/upload-image`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
      });
      setImageUrl(res.data.file_url);
    } catch (err) {
      setError('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedBillId) { setError('Please select a purchase bill'); return; }
    if (!amount || parseFloat(amount) <= 0) { setError('Please enter a valid amount'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/transactions`, {
        type: 'payment_out',
        reference_type: 'purchase_bill',
        reference_id: selectedBillId,
        amount: parseFloat(amount),
        payment_type: paymentType,
        date: paymentDate || null,
        reference_number: referenceNumber || null,
        description: formDescription || null,
        image_url: imageUrl || null,
        notes: notes || null
      }, getAuthHeaders());
      setShowModal(false);
      setSuccess('Payment recorded successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/transactions/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      setSuccess('Transaction reversed successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchData();
    } catch (err) {
      setError('Failed to delete transaction');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const filteredTxns = transactions.filter(t =>
    (t.reference_label || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.vendor_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.payment_type || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Only show bills that have a balance > 0, filtered by selected vendor
  const unpaidBills = bills.filter(b => (b.balance || 0) > 0 && (!selectedVendorId || b.vendor_id === selectedVendorId));

  return (
    <div data-testid="payment-out-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Payment Out</h1>
          <p className="text-slate-600 mt-1">Record payments against purchase bills</p>
        </div>
        <button onClick={openModal} data-testid="record-payment-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <Plus className="h-5 w-5" /> Record Payment
        </button>
      </div>

      {success && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-sm mb-6">
          <Check className="h-5 w-5" /><span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}
      {error && !showModal && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by bill number, vendor, or payment type..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Transaction History</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredTxns.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <CreditCard className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No payment transactions yet</h3>
            <p className="text-slate-500 mb-6">Record your first payment against a purchase bill</p>
            <button onClick={openModal}
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
              <Plus className="h-5 w-5" /> Record First Payment
            </button>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-9 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Bill No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Vendor</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Ref No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Payment Type</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Amount</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Description</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filteredTxns.map((txn) => (
              <div key={txn.id} data-testid={`txn-row-${txn.id}`}
                className="grid grid-cols-1 md:grid-cols-9 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="text-slate-600 text-sm">{txn.date ? formatDate(txn.date) : formatDateTime(txn.created_at)}</div>
                <div className="font-mono text-sm font-medium text-slate-900">{txn.reference_label || '-'}</div>
                <div className="text-slate-700 text-sm">{txn.vendor_name || '-'}</div>
                <div className="text-slate-600 text-sm font-mono">{txn.reference_number || '-'}</div>
                <div>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    {txn.payment_type}
                  </span>
                </div>
                <div className="font-medium text-red-600">- ₹{txn.amount?.toLocaleString()}</div>
                <div className="text-slate-500 text-sm truncate col-span-2">{txn.description || txn.notes || '-'}</div>
                <div>
                  <button onClick={() => setDeleteConfirm(txn.id)} data-testid={`delete-txn-${txn.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Reverse">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Record Payment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-3xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Record Payment Out</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" /><span>{error}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Select Vendor */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Select Vendor <span className="text-red-500">*</span>
                  </label>
                  <select value={selectedVendorId} onChange={(e) => { setSelectedVendorId(e.target.value); setSelectedBillId(''); }}
                    data-testid="select-vendor" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
                    <option value="">Select a vendor</option>
                    {vendors.filter(v => v.active).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>

                {/* Select Purchase Bill */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Select Purchase Bill <span className="text-red-500">*</span>
                  </label>
                  <select value={selectedBillId} onChange={(e) => setSelectedBillId(e.target.value)}
                    data-testid="select-bill" disabled={!selectedVendorId}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none disabled:bg-slate-100 disabled:cursor-not-allowed">
                    <option value="">Select a bill</option>
                    {unpaidBills.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.bill_number || 'No Bill #'} — Balance: ₹{b.balance?.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Bill Summary - spans full width */}
                {selectedBill && (
                  <div className="md:col-span-2 bg-slate-50 border border-slate-200 rounded-sm p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500 block">Vendor</span>
                        <span className="font-medium text-slate-900">{selectedBill.vendor_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Bill Total</span>
                        <span className="font-medium text-slate-900">₹{selectedBill.total?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Already Paid</span>
                        <span className="font-medium text-green-600">₹{selectedBill.paid_amount?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Balance Due</span>
                        <span className="font-bold text-red-600">₹{selectedBill.balance?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter payment amount" step="0.01" min="0"
                    data-testid="input-amount" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  {selectedBill && (
                    <button type="button" onClick={() => setAmount(String(selectedBill.balance))}
                      className="text-xs text-orange-600 hover:text-orange-700 mt-1">
                      Pay full balance (₹{selectedBill.balance?.toLocaleString()})
                    </button>
                  )}
                </div>

                {/* Payment Mode */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
                  <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)}
                    data-testid="select-payment-type" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
                    <option value="">Select</option>
                    {paymentModes.map(pm => <option key={pm.id} value={pm.name}>{pm.name}</option>)}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                  <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
                    data-testid="input-date" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>

                {/* Reference Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reference Number</label>
                  <input type="text" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="e.g., Cheque No., UTR, Transaction ID"
                    data-testid="input-reference-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={2}
                    placeholder="Payment description..."
                    data-testid="input-description" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none text-sm" />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Upload Image</label>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 cursor-pointer transition-colors text-sm">
                      <Upload className="h-4 w-4" />
                      {imageUrl ? 'Image Uploaded' : 'Choose File'}
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => { setImageFile(e.target.files[0]); handleImageUpload(e.target.files[0]); }} />
                    </label>
                    {uploadingImage && <Loader2 className="h-4 w-4 animate-spin text-orange-500" />}
                    {imageUrl && <span className="text-xs text-green-600">Uploaded</span>}
                  </div>
                  {imageUrl && (
                    <div className="mt-2">
                      <img src={`${BACKEND_URL}${imageUrl}`} alt="Receipt" className="h-20 w-auto rounded border border-slate-200" />
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                    placeholder="Optional notes..." data-testid="input-notes"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none text-sm" />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-payment-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Reverse Transaction</h3>
            <p className="text-slate-600 mb-6">This will reverse the payment and add the amount back to the bill balance. Continue?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-reverse-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Reverse</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
